import React, { useId } from 'react';
import { Image, StyleSheet, View } from 'react-native';
import Animated, { SharedValue, useAnimatedStyle } from 'react-native-reanimated';
import Svg, { ClipPath, Defs, G, LinearGradient, Rect, Stop } from 'react-native-svg';

import { CarouselCardBgPng } from '../../../assets';
import { carouselFocus } from '../common/carouselFocus';

const DESIGN_CARD_WIDTH = 226;
const RADIUS = 15;
const BORDER_COLOR = '#FFF908';
const BORDER_WIDTH = 1;
const FILL_TOP = '#970501';
const FILL_BOTTOM = '#220802';
const OUTER_GLOW = '#FF7200';
const OUTER_GLOW_RADIUS = 15;
const OUTER_GLOW_OPACITY = 0.7;
const BORDER_TURN_VERTICAL = 2.7618;
const BORDER_TURN_HORIZONTAL = 1.2533;

const GLOW_COLOR = '#FF0000';
const GLOW_SPREAD = 18;
const GLOW_STOPS = [
	[0, 0.58],
	[0.15, 0.447],
	[0.3, 0.332],
	[0.45, 0.228],
	[0.6, 0.134],
	[0.8, 0.044],
	[1, 0],
] as const;

type Props = {
	width: number;
	height: number;
	dimmed?: boolean;
	progress?: SharedValue<number>;
	index?: number;
	count?: number;
};

export const LobbyCardFrame = ({
	width,
	height,
	dimmed = false,
	progress,
	index = 0,
	count = 0,
}: Props) => {
	const uid = useId().replace(/[^a-zA-Z0-9]/g, '');

	const scale = width / DESIGN_CARD_WIDTH;
	const radius = RADIUS * scale;
	const stroke = BORDER_WIDTH * scale;

	const borderStyle = useAnimatedStyle(() => {
		const focus = progress ? carouselFocus(progress.value, index, count) : 1;
		const turn = 1 - focus;

		return {
			borderRadius: radius,
			borderLeftWidth: stroke * (1 + (BORDER_TURN_VERTICAL - 1) * turn),
			borderRightWidth: stroke * (1 + (BORDER_TURN_VERTICAL - 1) * turn),
			borderTopWidth: stroke * (1 + (BORDER_TURN_HORIZONTAL - 1) * turn),
			borderBottomWidth: stroke * (1 + (BORDER_TURN_HORIZONTAL - 1) * turn),
			opacity: dimmed ? 0.55 : 1,
		};
	}, [count, dimmed, index, progress, radius, stroke]);

	const litStyle = useAnimatedStyle(
		() => ({ opacity: progress ? carouselFocus(progress.value, index, count) : 0 }),
		[count, index, progress],
	);
	const darkStyle = useAnimatedStyle(
		() => ({ opacity: progress ? 1 - carouselFocus(progress.value, index, count) : 1 }),
		[count, index, progress],
	);

	if (width <= 0 || height <= 0) return null;

	const spread = Math.min(GLOW_SPREAD * scale, Math.min(width, height) / 2);

	const fillId = `cardFill${uid}`;
	const clipId = `cardClip${uid}`;
	const edges = [
		{ id: `glowT${uid}`, x1: '0', y1: '0', x2: '0', y2: '1', x: 0, y: 0, w: width, h: spread },
		{
			id: `glowB${uid}`,
			x1: '0',
			y1: '1',
			x2: '0',
			y2: '0',
			x: 0,
			y: height - spread,
			w: width,
			h: spread,
		},
		{ id: `glowL${uid}`, x1: '0', y1: '0', x2: '1', y2: '0', x: 0, y: 0, w: spread, h: height },
		{
			id: `glowR${uid}`,
			x1: '1',
			y1: '0',
			x2: '0',
			y2: '0',
			x: width - spread,
			y: 0,
			w: spread,
			h: height,
		},
	];

	return (
		<View
			pointerEvents='none'
			style={[
				styles.root,
				{
					width,
					height,
					borderRadius: radius,
					shadowColor: OUTER_GLOW,
					shadowOffset: { width: 0, height: 0 },
					shadowOpacity: dimmed ? OUTER_GLOW_OPACITY * 0.5 : OUTER_GLOW_OPACITY,
					shadowRadius: OUTER_GLOW_RADIUS * scale,
				},
			]}
		>
			<Animated.View
				pointerEvents='none'
				style={[
					styles.lit,
					{
						left: stroke,
						top: stroke,
						width: width - stroke * 2,
						height: height - stroke * 2,
						borderRadius: Math.max(radius - stroke, 0),
					},
					litStyle,
				]}
			>
				<Image source={CarouselCardBgPng} resizeMode='stretch' style={styles.fill} />
			</Animated.View>

			<Animated.View pointerEvents='none' style={[StyleSheet.absoluteFill, darkStyle]}>
				<Svg width={width} height={height}>
					<Defs>
						<LinearGradient id={fillId} x1='0' y1='0' x2='0' y2='1'>
							<Stop offset='0' stopColor={FILL_TOP} />
							<Stop offset='1' stopColor={FILL_BOTTOM} />
						</LinearGradient>
						<ClipPath id={clipId}>
							<Rect x={0} y={0} width={width} height={height} rx={radius} ry={radius} />
						</ClipPath>
					</Defs>
					<G clipPath={`url(#${clipId})`}>
						<Rect x={0} y={0} width={width} height={height} fill={`url(#${fillId})`} />
					</G>
				</Svg>
			</Animated.View>

			<Svg width={width} height={height} style={StyleSheet.absoluteFill} pointerEvents='none'>
				<Defs>
					{edges.map(e => (
						<LinearGradient key={e.id} id={e.id} x1={e.x1} y1={e.y1} x2={e.x2} y2={e.y2}>
							{GLOW_STOPS.map(([offset, opacity]) => (
								<Stop
									key={offset}
									offset={String(offset)}
									stopColor={GLOW_COLOR}
									stopOpacity={String(opacity)}
								/>
							))}
						</LinearGradient>
					))}
					<ClipPath id={`${clipId}e`}>
						<Rect x={0} y={0} width={width} height={height} rx={radius} ry={radius} />
					</ClipPath>
				</Defs>

				<G clipPath={`url(#${clipId}e)`}>
					{edges.map(e => (
						<Rect key={e.id} x={e.x} y={e.y} width={e.w} height={e.h} fill={`url(#${e.id})`} />
					))}
				</G>
			</Svg>

			<Animated.View pointerEvents='none' style={[styles.border, borderStyle]} />
		</View>
	);
};

const styles = StyleSheet.create({
	fill: {
		width: '100%',
		height: '100%',
	},
	lit: {
		position: 'absolute',
		overflow: 'hidden',
	},
	border: {
		...StyleSheet.absoluteFillObject,
		borderColor: BORDER_COLOR,
	},
	root: {
		...StyleSheet.absoluteFillObject,
		overflow: 'visible',
		elevation: 8,
	},
});

export default LobbyCardFrame;
